import {
  FavoriteBorder as AliveIcon,
  ViewList as CompareIcon,
  Email as EmailIcon,
  CheckCircle as HealthyIcon,
  History as HistoryIcon,
  School as SchoolIcon,
  Error as UnhealthyIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useHealthStatus, usePageMetadata } from "../hooks";
import { PevLogo } from "./PevLogo";

const navItems = [
  { label: "Validate", path: "/", icon: <EmailIcon /> },
  { label: "Compare", path: "/compare", icon: <CompareIcon /> },
  { label: "History", path: "/history", icon: <HistoryIcon /> },
  { label: "Learn", path: "/learn", icon: <SchoolIcon /> },
];

export function Layout() {
  usePageMetadata();

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isAlive,
    isLivenessLoading,
    isLivenessError,
    isReady,
    isReadinessLoading,
    version,
  } = useHealthStatus();

  const tabValue = navItems.findIndex(
    (item) => item.path === location.pathname,
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(navItems[newValue].path);
  };

  const renderHealthStatus = () => {
    // Still checking liveness
    if (isLivenessLoading) {
      return (
        <Chip
          icon={<CircularProgress size={14} color="inherit" />}
          label="Checking..."
          size="small"
          sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
        />
      );
    }

    // Liveness check failed - API is down
    if (isLivenessError || !isAlive) {
      return (
        <Chip
          icon={<UnhealthyIcon />}
          label="API Offline"
          size="small"
          color="error"
          sx={{ bgcolor: theme.palette.error.main }}
        />
      );
    }

    // Liveness passed, checking readiness
    if (isReadinessLoading) {
      return (
        <Chip
          icon={<AliveIcon />}
          label="Alive"
          size="small"
          sx={{ bgcolor: theme.palette.info.main, color: "white" }}
        />
      );
    }

    // Readiness check failed but still alive
    if (!isReady) {
      return (
        <Chip
          icon={<AliveIcon />}
          label="Alive (not ready)"
          size="small"
          sx={{ bgcolor: theme.palette.warning.main, color: "white" }}
        />
      );
    }

    // Fully healthy - show version
    return (
      <Chip
        icon={<HealthyIcon />}
        label={version ? `v${version}` : "Online"}
        size="small"
        sx={{ bgcolor: theme.palette.success.main, color: "white" }}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <PevLogo size={28} sx={{ mr: 1.5 }} />
          <Typography
            variant="h6"
            component="h1"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              display: { xs: "none", sm: "block" },
            }}
          >
            Pragmatic Email Validator
          </Typography>
          <Typography
            variant="h6"
            component="h1"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              display: { xs: "block", sm: "none" },
            }}
          >
            Email Validator
          </Typography>
          {renderHealthStatus()}
        </Toolbar>
        <Tabs
          value={tabValue >= 0 ? tabValue : false}
          onChange={handleTabChange}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            bgcolor: "rgba(0,0,0,0.1)",
            "& .MuiTab-root": {
              color: "rgba(255,255,255,0.7)",
              "&.Mui-selected": {
                color: "white",
              },
            },
            "& .MuiTabs-indicator": {
              bgcolor: "white",
            },
          }}
          aria-label="Navigation tabs"
        >
          {navItems.map((item) => (
            <Tab
              key={item.path}
              icon={item.icon}
              label={item.label}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          bgcolor: "background.default",
        }}
      >
        <Container maxWidth="lg">
          <Outlet />
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: "auto",
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Pragmatic Email Validator — Built with ❤️ for realistic email
          validation
        </Typography>
      </Box>
    </Box>
  );
}
